<script setup lang="ts">
import { ref } from 'vue';
import { DocumentDuplicateIcon, PrinterIcon, ArrowDownTrayIcon } from '@heroicons/vue/24/outline';
import { analytics } from '@/services/segment.ts';
import $events from '@/event-bus';

interface Props {
    codes: string[];
}

const props = defineProps<Props>();

const copied = ref(false);

const copyToClipboard = async (): Promise<void> => {
    const codesText = props.codes.join('\n');
    try {
        if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(codesText);
        } else {
            // Fallback for insecure context or unsupported browsers
            const textarea = document.createElement('textarea');
            textarea.value = codesText;
            textarea.style.position = 'fixed'; // Prevent scrolling to bottom
            textarea.style.opacity = '0';
            document.body.appendChild(textarea);
            textarea.focus();
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
        }
        copied.value = true;

        analytics.track('2FA Recovery Codes Copied');

        $events.$emit('toastNotification', {
            status: 'success',
            heading: 'Copied to clipboard!',
        });

        setTimeout(() => {
            copied.value = false;
        }, 2000);
    } catch (error) {
        console.error('Failed to copy codes:', error);
    }
};

const printCodes = (): void => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    analytics.track('2FA Recovery Codes Printed');

    const doc = printWindow.document;
    doc.title = 'Recovery Codes';
    doc.documentElement.lang = 'en';

    const style = doc.createElement('style');
    style.textContent = 'body { font-family: monospace; padding: 20px; } h1 { font-size: 18px; margin-bottom: 20px; } .code { margin: 10px 0; font-size: 14px; }';
    doc.head.appendChild(style);

    const h1 = doc.createElement('h1');
    h1.textContent = 'Clover Learning Two-Factor Authentication Recovery Codes';
    doc.body.appendChild(h1);

    props.codes.forEach(code => {
        const div = doc.createElement('div');
        div.className = 'code';
        div.textContent = code; // textContent prevents any injection
        doc.body.appendChild(div);
    });

    printWindow.print();
};

const downloadCodes = (): void => {
    const content = props.codes.join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'recovery-codes.txt';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    analytics.track('2FA Recovery Codes Downloaded');
};
</script>

<template>
    <div class="space-y-4">
        <div class="rounded-lg border border-gray-300 bg-gray-50 p-6 ">
            <div class="grid grid-cols-1 gap-2 font-mono text-sm ">
                <div
                    v-for="code in codes"
                    :key="code"
                    class="rounded bg-white px-3 py-2 text-xs text-gray-900"
                >
                    {{ code }}
                </div>
            </div>
        </div>

        <div class="flex flex-wrap gap-2">
            <button
                type="button"
                @click="copyToClipboard"
                class="inline-flex items-center gap-1 rounded-md bg-white border-gray-500 px-4 py-2 text-xs font-medium text-gray-700 border transition hover:bg-gray-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
                <DocumentDuplicateIcon class="h-4 w-4" />
                Copy<span class="hidden md:inline"> Codes</span>
            </button>

            <button
                type="button"
                @click="printCodes"
                class="inline-flex gap-2 items-center rounded-md border border-gray-500 bg-white px-4 py-2 text-xs font-medium text-gray-700 transition hover:bg-gray-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
                <PrinterIcon class="h-4 w-4" />
                Print<span class="hidden md:inline"> Codes</span>
            </button>

            <button
                type="button"
                @click="downloadCodes"
                class="inline-flex gap-2 items-center rounded-md border border-gray-500 bg-white px-4 py-2 text-xs font-medium text-gray-700 transition hover:bg-gray-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
                <ArrowDownTrayIcon class="h-4 w-4" />
                Download<span class="hidden md:inline"> Codes</span>
            </button>
        </div>
    </div>
</template>
